import { inject, Injectable, signal } from "@angular/core";
import { BehaviorSubject, firstValueFrom } from "rxjs";
import { Creneau } from "../models/creneau.model";
import { HttpClient, HttpParams } from "@angular/common/http";
import { environment } from "environments/environment";
import { OfService } from "app/features/of/services/of.service";
import { safeFirstValueFrom } from "../../../shared/utils/safe-first-value-from";
import { ActivityInfos } from "../../activity_infos/models/activity_infos.model";
import { TypePdf } from "../../documents/enums/type-pdf.enum";
import { printPdf } from "../../../shared/utils/print-pdf.utils";
import { OfEtat } from "../../of/enums/etat-of.enum";
import { Ligne } from "../../ligne/models/ligne.model";
import { DialogsService } from "../../../shared/services/dialogs/dialogs.service";

@Injectable({
  providedIn: "root",
})
export class CreneauLigneService {
  public creneaux$ = new BehaviorSubject<Creneau[]>([]);
  public _creneauSaving$ = new BehaviorSubject<boolean>(false);
  public _creneauSaved$ = new BehaviorSubject<boolean>(false);
  dialogService = inject(DialogsService);

  constructor(
    private http: HttpClient,
    private ofService: OfService,
  ) {}

  loading = signal(false);
  async loadAllCreneaux(options?: { ligneId?: string }) {
    try {
      let params = new HttpParams();
      if (options?.ligneId) {
        params = params.set("ligneId", options.ligneId);
      }
      this.loading.set(true);
      const creneaux = await safeFirstValueFrom(
        this.http.get<Creneau[]>(`${environment.api()}/creneau`, { params }),
      );
      if (creneaux) {
        this.creneaux$.next(creneaux);
      }
    } catch (error) {
    } finally {
      this.loading.set(false);
    }
  }

  async saveCreneau(a: Creneau) {
    this._creneauSaving$.next(true);
    this._creneauSaved$.next(false);
    const savedCreneau = await safeFirstValueFrom(
      this.http.post<Creneau>(`${environment.api()}/creneau`, a),
    );

    this._creneauSaving$.next(false);
    this._creneauSaved$.next(true);
    this.updateLocalList(savedCreneau);
    this.ofService.loadAllToPlan();
  }

  private updateLocalList(creneau: Creneau): void {
    let creneaux = [...this.creneaux$.value];
    const foundIndex = creneaux.findIndex((c) => c.id === creneau.id);
    if (foundIndex >= 0) {
      creneaux[foundIndex] = creneau;
    } else {
      creneaux.push(creneau);
    }

    this.creneaux$.next([...creneaux]);
  }

  async saveCreneauxLigne(
    creneauxToSave: Creneau[],
    ligneId: string,
  ): Promise<Creneau[]> {
    const toSave = creneauxToSave.map((c) => ({ ...c, ligne: undefined }));
    const creneauxSaved = await safeFirstValueFrom(
      this.http.post<Creneau[]>(
        `${environment.api()}/creneau/planify/ligne/${ligneId}`,
        toSave,
      ),
    );
    creneauxSaved.forEach((c) => this.updateLocalList(c));
    return creneauxSaved;
  }

  async deleteCreneau(creneau: Creneau) {
    this.http
      .delete<Creneau>(`${environment.api()}/creneau/${creneau.id}`)
      .subscribe(() => {
        this._creneauSaving$.next(false);
        this._creneauSaved$.next(true);
        this.creneaux$.next(
          this.creneaux$.value.filter((c) => c.id !== creneau?.id),
        );
        this.ofService.loadAllToPlan();
      });
  }

  async printSuiviProd(lignes: string[]) {
    await this.dialogService.openPdfLoader(async () => {
      const contentPdf = await firstValueFrom(
        this.http.post<{ activityInfos: ActivityInfos; lignes: any }>(
          `${environment.api()}/creneau/pdf-suivi-prod`,
          {
            lignes,
          },
        ),
      );
      const pdf = await firstValueFrom(
        this.http.post(
          `${environment.pdfApi()}/pdf/${TypePdf.SUIVI_PRODUCTION}`,
          contentPdf,
          {
            responseType: "blob",
          },
        ),
      );
      const file = new Blob([pdf], { type: "application/pdf" });
      printPdf(URL.createObjectURL(file), 'Suivi Production');
    });
  }

  buildSuiviFabrication(lignes: Ligne[], creneaux: Creneau[]) {
    return lignes.map((l) => {
      const creneauxLigne = creneaux.filter((c) => c.ligneId == l.id);
      let ofEnCours = null;
      let nbOfEnCours = 0;
      const creneauProd = creneauxLigne.filter(
        (c) => c.of?.etat === OfEtat.EN_PROD,
      );
      const creneauAttenteProd = creneauxLigne.filter(
        (c) => c.of?.etat === OfEtat.EN_ATTENTE_PROD,
      );
      if (creneauProd.length) {
        ofEnCours = creneauProd[0].of;
        nbOfEnCours = creneauProd.length;
      } else if (creneauAttenteProd.length) {
        nbOfEnCours = new Set(creneauAttenteProd.map((c) => c.indexParallele))
          .size;
        ofEnCours = creneauAttenteProd[0].of;
      }
      return {
        ligne: l,
        ofEnCours,
        creneauxEnCours: creneauProd,
        creneauxEnAttente: creneauAttenteProd,
        numeroOfsEnCours: creneauProd.map((c) => c.of?.numero).filter(Boolean),
        numeroOfsEnAttente: creneauAttenteProd
          .map((c) => c.of?.numero)
          .filter(Boolean),
        nbOfEnCours,
        selected: signal<boolean>(
          !!(creneauAttenteProd.length || creneauProd.length),
        ),
      };
    });
  }
}
